import React from "react";

import { TOM_STATUS_LIST } from "./Common";

import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_TIME_FORMAT } from "src/common/constants";

import { LEGITIMATION_STATUS_CONFIG } from "../pages/admin/data-subject-request/components/legitimation/constants";

import {
    getHistoryLogQuestionnaireLabel,
    getHistoryLogCaseIdLabel,
    renderHistoryLogQuestionnaireChanges,
    renderHistoryLogAssetStatusWasNow,
    renderHistoryLogCaseStatusWasNow,
    renderHistoryLogTaskStatusWasNow,
    renderHistoryLogPriorityWasNow,
    renderHistoryLogActiveInactiveWasNow,
    renderHistoryLogApplicableWasNow,
    renderHistoryLogTaskId,
    renderHistoryLogRiskId,
    renderHistoryLogPlainText,
    renderHistoryLogAttachments,
    renderHistoryLogFileSize,
} from "../helpers/common";

const MailSettingsKeysTitles = {
    emailAddress: "From Email Address",
    displayName: "Sender Name",
    smtpServer: "SMTP Server",
    smtpPort: "SMTP Port",
    smtpEncryption: "Encryption Method",
    smtpUsername: "SMTP Username",
    authModeOut: "Authentication Mode",
};

const dateUtils = new DateUtils();

const renderHistoryLogDateTime = ({ value }) => {
    if (!value) {
        return undefined;
    }

    return (
        <span className="detail-value light">
            <i className="ri-time-line"></i>&nbsp;
            {dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_TIME_FORMAT)}
        </span>
    );
};

const renderHistoryLogWasNowValue = ({
    otherFields,
    t,
    previousKey,
    currentKey,
    formatValue,
}) => {
    const was = otherFields?.[previousKey];
    const now = otherFields?.[currentKey];
    const wasDefined = typeof was !== "undefined";
    const nowDefined = typeof now !== "undefined";

    const format = (value) => {
        if (typeof formatValue === "function") {
            return formatValue(value, t);
        }

        if (typeof value === "boolean") {
            return t(value ? "Yes" : "No");
        }

        if (value === null || typeof value === "undefined" || value === "") {
            return "-";
        }

        return String(value);
    };

    if (wasDefined && nowDefined) {
        return (
            <>
                {t("Before")}: {format(was)} | {t("After")}: {format(now)}
            </>
        );
    }

    if (nowDefined) {
        return format(now);
    }

    if (wasDefined) {
        return format(was);
    }

    return undefined;
};

const renderHistoryLogBooleanWasNow = ({
    otherFields,
    t,
    previousKey,
    currentKey,
    trueLabel = "Yes",
    falseLabel = "No",
}) => {
    return renderHistoryLogWasNowValue({
        otherFields,
        t,
        previousKey,
        currentKey,
        formatValue: (value, translate) => {
            if (typeof value !== "boolean") {
                return value === null || typeof value === "undefined"
                    ? "-"
                    : String(value);
            }

            return translate(value ? trueLabel : falseLabel);
        },
    });
};

const renderHistoryLogLongTextWasNow = ({
    otherFields,
    t,
    previousKey,
    currentKey,
}) => {
    const was = otherFields?.[previousKey];
    const now = otherFields?.[currentKey];
    const wasDefined = typeof was !== "undefined" && was !== null;
    const nowDefined = typeof now !== "undefined" && now !== null;

    if (!wasDefined && !nowDefined) {
        return undefined;
    }

    const textStyle = {
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        display: "block",
    };

    if (wasDefined && nowDefined) {
        return (
            <div className="detail-value">
                <div className="mb-2">
                    <strong>{t("Before")}:</strong>
                    <span style={textStyle}>{was || "-"}</span>
                </div>
                <div>
                    <strong>{t("After")}:</strong>
                    <span style={textStyle}>{now || "-"}</span>
                </div>
            </div>
        );
    }

    return (
        <span className="detail-value" style={textStyle}>
            {nowDefined ? now || "-" : was || "-"}
        </span>
    );
};

const renderHistoryLogCapitalizedValue = ({ value, t }) => {
    if (value === null || typeof value === "undefined" || value === "") {
        return undefined;
    }

    const label = String(value);
    const capitalized = label.charAt(0).toUpperCase() + label.slice(1);

    return t(capitalized);
};

export const ActionsMetaData = {
    admin: {
        activate_framework: [
    {
        key: "added_framework",
        title: "Activated Framework",
        valueRenderer: ({ value, t }) => {
            return value ? t(value) : undefined;
        },
        hideIfValueIsUndefined: true,
    },
],
        add_legal_entities_to_framework: [
    {
        key: "framework",
        title: "Framework",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ value, t }) => {
            return value ? t(value) : undefined;
        },
    },
    {
        key: "legal_entity_name",
        title: "Legal Entity",
        hideIfValueIsUndefined: true,
    },
    {
        key: "added_legal_entity",
        title: "Added Legal Entity ID",
        hideIfValueIsUndefined: true,
    },
],
        change_active_directory_settings: [],
        change_default_owner: [
            {
                key: "added_owner_id",
                title: "Added Owner Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "added_owner_name",
                title: "Added Owner Name",
                hideIfValueIsUndefined: true,
            }
        ],
        change_user_status: [
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogActiveInactiveWasNow,
            },
            {
                key: "changed_user_id",
                title: "Changed User ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changed_user_name",
                title: "Changed User Name",
                hideIfValueIsUndefined: true,
            }
        ],
        create_user: [
            {
                key: "created_users",
                title: "Created Users",
                hideIfValueIsUndefined: true,
            }
        ],
        deactivate_framework: [
    {
        key: "removed_framework",
        title: "Deactivated Framework",
        valueRenderer: ({ value, t }) => {
            return value ? t(value) : undefined;
        },
        hideIfValueIsUndefined: true,
    },
],
        delete_user: [
            {
                key: "deleted_user_id",
                title: "Deleted User Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "deleted_user_name",
                title: "Deleted User Name",
                hideIfValueIsUndefined: true,
            }
        ],
        edit_mailer_setting: [
    {
        key: "changes",
        title: "Changes",
        valueRenderer: ({ value, t }) => {
            return (
                <div className="d-flex justify-content-center align-items-start flex-column">
                    {(value || []).map((item, index) => {
                        return (
                            <p key={index} className="mb-2">
                                <i className="ri-checkbox-blank-circle-fill me-1 align-middle text-info font-size-12" />
                                {t(MailSettingsKeysTitles[item.field])} {" >> "} {t("Before")}: {item.old_value} | {t("After")}: {item.new_value}
                            </p>
                        );
                    })}
                </div>
            );
        },
    },
],
        edit_news_setting: [],
        edit_organization: [],
        edit_risk_matrix: [],
        edit_user: [
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const fieldLabels = {
                        phoneNumber: t("Office Number"),
                        mobileNumber: t("Mobile Number"),
                    };
                    const changes = otherFields?.changes;

                    if (changes && typeof changes === "object" && !Array.isArray(changes)) {
                        const remappedChanges = Object.fromEntries(
                            Object.entries(changes).map(([key, change]) => [
                                fieldLabels[key] || key,
                                change,
                            ])
                        );

                        return renderHistoryLogQuestionnaireChanges(
                            { ...otherFields, changes: remappedChanges },
                            t
                        );
                    }

                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
            {
                key: "edited_user_id",
                title: "Edited User Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "edited_user_name",
                title: "Edited User Name",
                hideIfValueIsUndefined: true,
            }
        ],
        login: [],
        remove_legal_entities_from_framework: [
    {
        key: "framework",
        title: "Framework",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ value, t }) => {
            return value ? t(value) : undefined;
        },
    },
    {
        key: "legal_entity_name",
        title: "Legal Entity",
        hideIfValueIsUndefined: true,
    },
    {
        key: "removed_legal_entity",
        title: "Removed Legal Entity ID",
        hideIfValueIsUndefined: true,
    },
],
        remove_option: [
            {
                key: "question",
                title: "Question Text",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_question: [
            {
                key: "question",
                title: "Question Text",
                hideIfValueIsUndefined: true,
            }
        ],
        restore_news_setting: [],
        restore_organization_email_notifications: [
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    if (!value || Array.isArray(value) || !Object.keys(value).length) {
                        return undefined;
                    }

                    return (
                        <div className="d-flex justify-content-center align-items-start flex-column">
                            {Object.entries(value).map(([label, change]) => (
                                <p key={label} className="mb-2">
                                    <i className="ri-checkbox-blank-circle-fill me-1 align-middle text-info font-size-12" />
                                    {t(label)}: {t(change?.from)} → {t(change?.to)}
                                </p>
                            ))}
                        </div>
                    );
                },
            },
        ],
        restore_risk_matrix: [],
        toggle_mailer_setting_active: [
    {
        key: "previous_status",
        title: "Previous Status",
        valueRenderer: ({ value, t }) => {
            return t(value ? "Active" : "Inactive");
        },
    },
    {
        key: "new_status",
        title: "New Status",
        valueRenderer: ({ value, t }) => {
            return t(value ? "Active" : "Inactive");
        },
    },
],
        update_framework_settings: [],
        update_organization_email_notifications: [
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    if (!value || Array.isArray(value) || !Object.keys(value).length) {
                        return undefined;
                    }

                    return (
                        <div className="d-flex justify-content-center align-items-start flex-column">
                            {Object.entries(value).map(([label, change]) => (
                                <p key={label} className="mb-2">
                                    <i className="ri-checkbox-blank-circle-fill me-1 align-middle text-info font-size-12" />
                                    {t(label)}: {t(change?.from)} → {t(change?.to)}
                                </p>
                            ))}
                        </div>
                    );
                },
            },
        ],
        update_question: [
    {
        key: "changes",
        title: "Changes",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ otherFields, t }) => {
            return renderHistoryLogQuestionnaireChanges(otherFields, t);
        },
    },
],
    },
    asset_document: {
        create_asset_document: [
            {
                key: "asset_document_name",
                title: "Uploaded Document",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                            key: "asset_document_size",
                            title: "Asset Document Size",
                            hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogFileSize,
                        },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "software_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        }
        ],
        delete_asset_document: [
            {
                key: "asset_document_name",
                title: "Removed Document",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "software_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        }
        ],
        update_asset_document: [
            {
                key: "asset_document_name",
                title: "Asset Document Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "asset_document_size",
                title: "Asset Document Size",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogFileSize,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    case: {
        add_comment: [
            {
                key: "comment",
                title: "Comment",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPlainText,
            },
            {
                key: "files",
                title: "Files",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            {
                key: "type",
                title: "Type",
                hideIfValueIsUndefined: true,
            }
        ],
        add_internal_comment: [
            {
                key: "comment",
                title: "Comment",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPlainText,
            },
            {
                key: "files",
                title: "Files",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        add_related_asset: [
            {
                key: "assetName",
                title: "Related Asset Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        add_report_legal_entity: [
            {
                key: "added_legal",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                            key: "added_legal_id",
                            title: "Added Legal Id",
                            hideIfValueIsUndefined: true,
                        },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "questionnaire",
                            title: "Questionnaire",
                            hideIfValueIsUndefined: true,
                            valueRenderer: ({ otherFields, t }) => {
                                return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                                    undefined;
                            },
                        }
        ],
        change_admin_category: [
            {
                key: "added_category",
                title: "Added Category",
                hideIfValueIsUndefined: true,
            },
            {
                key: "removed_category",
                title: "Removed Category",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        change_admin_description: [
            {
                key: "description",
                title: "Description",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPlainText,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        create: [
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            {
                key: "type",
                title: "Type",
                hideIfValueIsUndefined: true,
            }
        ],
        delete: [
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        delete_attachment: [
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },

            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            }
        ],
        download_analyst_attachment: [
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        download_user_attachment: [
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        dsr_generate_lia: [
            {
                            key: "ai_success",
                            title: "AI Success",
                            hideIfValueIsUndefined: true,
                        },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "questionnaire",
                            title: "Questionnaire",
                            hideIfValueIsUndefined: true,
                            valueRenderer: ({ otherFields, t }) => {
                                return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                                    undefined;
                            },
                        }
        ],
        dsr_generate_response: [
            {
                            key: "include_internal_comments",
                            title: "Include Internal Comments",
                            hideIfValueIsUndefined: true,
                        },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "questionnaire",
                            title: "Questionnaire",
                            hideIfValueIsUndefined: true,
                            valueRenderer: ({ otherFields, t }) => {
                                return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                                    undefined;
                            },
                        },
            {
                            key: "target",
                            title: "Target",
                            hideIfValueIsUndefined: true,
                        }
        ],
        dsr_update_workflow: [
            {
                key: "subject_relationship",
                title: "Subject Relationship",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    const labels = {
                        customer: "Customer",
                        supplier: "Supplier",
                        visitor: "Visitor",
                        employee: "Employee",
                        other: "Other",
                    };
                    return value ? t(labels[value] || value) : undefined;
                },
            },
            {
                key: "lawful_basis",
                title: "Lawful Basis",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    const labels = {
                        legal_obligation: "Legal Obligation",
                        legitimate_interest: "Legitimate Interest",
                        consent: "Consent",
                        contract: "Contract",
                        public_task: "Public Task",
                        vital_interests: "Vital Interests",
                    };
                    return value ? t(labels[value] || value) : undefined;
                },
            },
            {
                key: "legitimation_status",
                title: "Legitimation status",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    if (!value) {
                        return undefined;
                    }
                    const config = LEGITIMATION_STATUS_CONFIG[value];
                    return config ? t(config.labelKey) : value;
                },
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "questionnaire",
                            title: "Questionnaire",
                            hideIfValueIsUndefined: true,
                            valueRenderer: ({ otherFields, t }) => {
                                return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                                    undefined;
                            },
                        }
        ],
        link_case: [
            {
                key: "link_case_case_id",
                title: "Link Case Case Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "link_case_secondary_id",
                title: "Linked Case Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        mark_as_delete: [
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        remove_related_asset: [
            {
                key: "assetName",
                title: "Related Asset Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        remove_report_legal_entity: [
            {
                key: "removed_legal",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "questionnaire",
                            title: "Questionnaire",
                            hideIfValueIsUndefined: true,
                            valueRenderer: ({ otherFields, t }) => {
                                return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                                    undefined;
                            },
                        },
            {
                            key: "removed_legal_id",
                            title: "Removed Legal Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        toggle_test_case: [
            {
                key: "new_status",
                title: "New Status",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        update_analyst: [
            {
                key: "analyst_name",
                title: "Analyst",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.previous_analyst_name;
                    const now = otherFields?.analyst_name ?? otherFields?.changed_user_name;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        update_analyst_readonly: [
            {
                key: "added_analyst",
                title: "Added Analyst",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    if (!value) {
                        return undefined;
                    }

                    if (typeof value === "object") {
                        return value.full_name || value.name || undefined;
                    }

                    return String(value);
                },
            },
            {
                key: "removed_analyst",
                title: "Removed Analyst",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    if (!value) {
                        return undefined;
                    }

                    if (typeof value === "object") {
                        return value.full_name || value.name || undefined;
                    }

                    return String(value);
                },
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        update_priority: [
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
        ],
        update_questionnaire: [
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
        ],
        update_risk: [
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            {
                key: "risk",
                title: "New Risk",
                hideIfValueIsUndefined: true,
            }
        ],
        update_status: [
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogCaseStatusWasNow,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            }
        ],
        upload_attachment: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "secondary_case_id",
                title: "Case ID",
                titleRenderer: ({ otherFields, t }) => getHistoryLogCaseIdLabel(otherFields, t),
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            {
                key: "type",
                title: "Type",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    client_download: {
        download_client: [],
    },
    control: {
        toggle_applicable: [
            {
                key: "applicable",
                title: "Applicable",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogApplicableWasNow,
            },
            {
                key: "control_id",
                title: "Control ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "reason",
                title: "Reason",
                hideIfValueIsUndefined: true,
            },
            {
                key: "soa_version_id",
                title: "SOA Version Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "soa_version_number",
                title: "SOA Version Number",
                hideIfValueIsUndefined: true,
            }
        ],
        update_justification: [
            {
                key: "control_id",
                title: "Control ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "reason",
                title: "Reason",
                hideIfValueIsUndefined: true,
            },
            {
                key: "soa_version_id",
                title: "SOA Version Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "soa_version_number",
                title: "SOA Version Number",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    document: {
        create: [
            {
                key: "id",
                title: "Entity Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "title",
                title: "Title",
                hideIfValueIsUndefined: true,
            }
        ],
        remove: [],
        remove_category: [
            {
                key: "title",
                title: "Title",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    entityManagement: {
        add_bulk_legal_entity: [
            {
                key: "file_uploaded",
                title: "File Uploaded",
                hideIfValueIsUndefined: true,
            },
            {
                key: "total_records",
                title: "Total Records",
                hideIfValueIsUndefined: true,
            }
        ],
        add_division: [
            {
                key: "division_id",
                title: "Department Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "division_title",
                title: "Department Title",
                hideIfValueIsUndefined: true,
            },
            {
                key: "institute",
                title: "Institute",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            }
        ],
        add_institute: [
            {
                key: "institute_id",
                title: "Institute Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "institute_title",
                title: "Institute Title",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legalEntityTitle",
                title: "Legal Entity Title",
                hideIfValueIsUndefined: true,
            }
        ],
        add_legal_entity: [
            {
                key: "legal_entity_secondary_id",
                title: "Legal Entity Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity_title",
                title: "Legal Entity Title",
                hideIfValueIsUndefined: true,
            },
            {
                key: "short_name",
                title: "Short Name",
                hideIfValueIsUndefined: true,
            }
        ],
        add_legal_location: [
            {
                key: "address",
                title: "Address",
                hideIfValueIsUndefined: true,
            },
            {
                key: "location_id",
                title: "Location Id",
                hideIfValueIsUndefined: true,
            }
        ],
        clear_routing: [
            {
                key: "legal_entity_secondary_id",
                title: "Legal Entity Id",
                hideIfValueIsUndefined: true,
            }
        ],
        edit_division: [
            {
                key: "new_title",
                title: "Title",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_title;
                    const now = otherFields?.new_title;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "division_id",
                title: "Department Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "institute",
                title: "Institute",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            }
        ],
        edit_financial_setting: [],
        edit_institute: [
            {
                key: "institute_id",
                title: "Institute Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "institute_title",
                title: "Institute Title",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legalEntityTitle",
                title: "Legal Entity Title",
                hideIfValueIsUndefined: true,
            }
        ],
        edit_legal_entity: [
            {
                key: "legal_entity_id",
                title: "Legal Entity Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity_secondary_id",
                title: "Legal Entity Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity_title",
                title: "Legal Entity Title",
                hideIfValueIsUndefined: true,
            }
        ],
        delete: [
            {
                key: "legal_entity_id",
                title: "Legal Entity Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity_title",
                title: "Legal Entity Title",
                hideIfValueIsUndefined: true,
            }
        ],
        force_delete: [
            {
                key: "legal_entity_id",
                title: "Legal Entity Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity_title",
                title: "Legal Entity Title",
                hideIfValueIsUndefined: true,
            }
        ],
        edit_legal_location: [
            {
                key: "address",
                title: "Address",
                hideIfValueIsUndefined: true,
            },
            {
                key: "location_id",
                title: "Location Id",
                hideIfValueIsUndefined: true,
            }
        ],
        edit_routing: [],
        force_remove_legal_entity: [
            {
                key: "legal_entity_secondary_id",
                title: "Legal Entity Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity_title",
                title: "Legal Entity Title",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_division: [
            {
                key: "division_id",
                title: "Department Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "division_title",
                title: "Department Title",
                hideIfValueIsUndefined: true,
            },
            {
                key: "institute",
                title: "Institute",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_institute: [
            {
                key: "institute_id",
                title: "Institute Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "institute_title",
                title: "Institute Title",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legalEntityTitle",
                title: "Legal Entity Title",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_legal_entity: [
            {
                key: "legal_entity_secondary_id",
                title: "Legal Entity Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "legal_entity_title",
                title: "Legal Entity Title",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_legal_location: [
            {
                key: "address",
                title: "Address",
                hideIfValueIsUndefined: true,
            },
            {
                key: "location_id",
                title: "Location Id",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    hardware: {
        add_legal_entity: [
            {
                key: "legal_entity_name",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            },
            {
                key: "software_secondary_id",
                title: "Software ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
            },
            {
                            key: "hardware_secondary_id",
                            title: "Hardware Id",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "legal_entity_secondary_id",
                            title: "Legal Entity Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        change_analyst: [
            {
                key: "changed_user_name",
                title: "Analyst",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.previous_analyst_name;
                    const now = otherFields?.analyst_name ?? otherFields?.changed_user_name;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "hardware_secondary_id",
                title: "Hardware Id",
                hideIfValueIsUndefined: true,
            },
        ],
        change_priority: [
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAssetStatusWasNow,
            },
            {
                key: "status",
                title: "Priority",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPriorityWasNow,
            },
            {
                key: "hardware_secondary_id",
                title: "Hardware Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_status: [
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAssetStatusWasNow,
            },
            {
                key: "hardware_secondary_id",
                title: "Hardware Id",
                hideIfValueIsUndefined: true,
            }
        ],
        create: [
            {
                key: "hardware_ids",
                title: "Created Hardware IDs",
                hideIfValueIsUndefined: true,
            }
        ],
        delete: [
            {
                key: "hardware_secondary_id",
                title: "Hardware Id",
                hideIfValueIsUndefined: true,
            }
        ],
        download: [],
        edit: [
            {
                key: "new_operating_system",
                title: "Operating System",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_operating_system;
                    const now = otherFields?.new_operating_system;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "new_manufacturer",
                title: "Manufacturer",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_manufacturer;
                    const now = otherFields?.new_manufacturer;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "new_model",
                title: "Model",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_model;
                    const now = otherFields?.new_model;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "hardware_secondary_id",
                title: "Hardware Id",
                hideIfValueIsUndefined: true,
            }
        ],
        handover: [
            {
                key: "handover_at",
                title: "Handover At",
                hideIfValueIsUndefined: true,
            },
            {
                key: "handover_to",
                title: "Handover To",
                hideIfValueIsUndefined: true,
            },
            {
                key: "hardware_secondary_id",
                title: "Hardware Id",
                hideIfValueIsUndefined: true,
            }
        ],
        multi_approve: [
            {
                key: "hardware_ids",
                title: "Created Hardware IDs",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_legal_entity: [
            {
                key: "legal_entity_name",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            },
            {
                key: "software_secondary_id",
                title: "Software ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
            },
            {
                            key: "hardware_secondary_id",
                            title: "Hardware Id",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "legal_entity_secondary_id",
                            title: "Legal Entity Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        toggle_flag: [
            {
                key: "hardware_secondary_id",
                title: "Hardware Id",
                hideIfValueIsUndefined: true,
            }
        ],
        upload_csv: [
            {
                key: "hardware_ids",
                title: "Created Hardware IDs",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    other_asset: {
        add_comment: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        add_dpa_attachment: [
            {
                key: "filename",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields }) => {
                    return otherFields?.filename || otherFields?.file_name || otherFields?.origin_name || otherFields?.original_name || otherFields?.name || undefined;
                },
            },
            {
                key: "software_secondary_id",
                title: "Software ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "dpa_attachment_content_type",
                            title: "DPA Attachment Content Type",
                            hideIfValueIsUndefined: true,
                        },
            {
                key: "dpa_attachment_id",
                            title: "DPA Attachment Id",
                            hideIfValueIsUndefined: true,
                        },
            {
                key: "dpa_attachment_name",
                            title: "DPA Attachment Name",
                            hideIfValueIsUndefined: true,
                        },
            {
                key: "dpa_attachment_size",
                            title: "DPA Attachment Size",
                            hideIfValueIsUndefined: true,
                        }
        ],
        add_internal_comment: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        add_legal_entity: [
            {
                key: "legal_entity_name",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            },
            {
                key: "software_secondary_id",
                title: "Software ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
            },
            {
                key: "legal_entity_secondary_id",
                            title: "Legal Entity Id",
                            hideIfValueIsUndefined: true,
                        },
            {
                key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        }
        ],
        add_module: [
            {
                key: "module",
                title: "Module",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        add_processing_activity_module_name: [
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        add_related_asset: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        add_report_legal_entity: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        change_admin_category: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        change_admin_description: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        change_analyst: [
            {
                key: "changed_user_name",
                title: "Analyst",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.previous_analyst_name;
                    const now = otherFields?.analyst_name ?? otherFields?.changed_user_name;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        change_company: [
            {
                key: "company",
                title: "Company",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        change_email: [
            {
                key: "email",
                title: "Email",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        change_phone_number: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "phone_number",
                title: "Phone Number",
                hideIfValueIsUndefined: true,
            }
        ],
        change_position: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "position",
                title: "Position",
                hideIfValueIsUndefined: true,
            }
        ],
        change_priority: [
            {
                key: "priority",
                title: "Priority",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPriorityWasNow,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        change_processing_activity: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        change_responsible_department: [
            {
                key: "department",
                title: "Department",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        change_responsible_person: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "user",
                title: "User",
                hideIfValueIsUndefined: true,
            }
        ],
        change_statement_link: [
            {
                key: "link",
                title: "Link",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        change_status: [
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAssetStatusWasNow,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        create: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        create_ai_processing_activity: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        create_processing_activity: [
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        delete: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        delete_asset_document: [
            {
                key: "asset_document_name",
                title: "Removed Document",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                key: "software_secondary_id",
                            title: "Software Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        delete_attachment: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        download: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        download_analyst_attachment: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        download_user_attachment: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        dpia: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        edit: [
            {
                key: "new_title",
                title: "Title",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_title;
                    const now = otherFields?.new_title;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "new_type",
                title: "Type",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_type;
                    const now = otherFields?.new_type;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "new_description",
                title: "Description",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_description;
                    const now = otherFields?.new_description;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        link_case: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        mark_as_delete: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        multi_approve: [
            {
                key: "other_asset_ids",
                title: "Other Asset Ids",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value?.length ? value.join(", ") : undefined;
                },
            }
        ],
        remove_legal_entity: [
            {
                key: "legal_entity_name",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            },
            {
                key: "software_secondary_id",
                title: "Software ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
            },
            {
                key: "legal_entity_secondary_id",
                            title: "Legal Entity Id",
                            hideIfValueIsUndefined: true,
                        },
            {
                key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        }
        ],
        remove_module: [
            {
                key: "module",
                title: "Module",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_processing_activity: [
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_related_asset: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        remove_report_legal_entity: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        toggle_dpa_consultation_required: [
    {
        key: "previous_status",
        title: "Consultation Required",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ otherFields, t }) => {
            if (
                typeof otherFields?.previous_status === "undefined" &&
                typeof otherFields?.new_status === "undefined"
            ) {
                if (typeof otherFields?.is_consultation_required === "boolean") {
                    return otherFields.is_consultation_required ? t("Yes") : t("No");
                }
                return undefined;
            }
            return (
                <>
                    {t("Before")}: {t(otherFields?.previous_status ? "Yes" : "No")} | {t("After")}: {t(otherFields?.new_status ? "Yes" : "No")}
                </>
            );
        },
    },
    {
        key: "software_secondary_id",
        title: "Software ID",
        hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
    },
    {
        key: "other_asset_secondary_id",
        title: "Asset ID",
        hideIfValueIsUndefined: true,
    },
],
        toggle_flag: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        toggle_inactive_status: [
            {
                key: "is_inactive",
                title: "Is Inactive",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        toggle_test_case: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        update: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        update_analyst: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        update_analyst_readonly: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        update_data_protection_impact_assessment: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_description_of_processing: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_determining_the_processing: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_dpa_date: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        update_dpa_reason: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        update_dpa_result: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        update_processing_activity_module_name: [
            {
                key: "to_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.from_name;
                    const now = otherFields?.to_name;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        update_responsible_person: [
            {
                key: "to_user",
                title: "Responsible Person",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.from_user;
                    const now = otherFields?.to_user;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "report_case",
                title: "Report Case Id",
                hideIfValueIsUndefined: true,
            }
        ],
        update_risk: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        update_risk_assessment: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_status: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        upload_attachment: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    questionnaire: {
        remove_option: [
            {
                key: "question",
                title: "Question Text",
                hideIfValueIsUndefined: true,
            },
        ],
        remove_question: [
            {
                key: "question",
                title: "Question Text",
                hideIfValueIsUndefined: true,
            },
        ],
        update_question: [
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
    },
    risk: {
        add_legal_entity: [
            {
                key: "legal_entity_name",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            },
            {
                key: "software_secondary_id",
                title: "Software ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
            },
            {
                            key: "legal_entity_secondary_id",
                            title: "Legal Entity Id",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        }
        ],
        assign_analyst: [
            {
                key: "analyst_name",
                title: "Analyst",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.previous_analyst_name;
                    const now = otherFields?.analyst_name ?? otherFields?.changed_user_name;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        create: [
            {
                key: "attachments",
                title: "Attachments",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        create_comment: [
            {
                key: "attachments",
                title: "Attachments",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "comment",
                title: "Comment",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPlainText,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        delete: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        delete_file: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        download_file: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        download_files: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        remove_legal_entity: [
            {
                key: "legal_entity_name",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            },
            {
                key: "software_secondary_id",
                title: "Software ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
            },
            {
                            key: "legal_entity_secondary_id",
                            title: "Legal Entity Id",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        }
        ],
        update_acceptance: [
            {
                key: "accepted_at",
                title: "Accepted At",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogDateTime,
            },
            {
                key: "accepted_by",
                title: "Accepted By",
                hideIfValueIsUndefined: true,
            },
            {
                key: "is_accepted",
                title: "Is Accepted",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "reason",
                title: "Reason",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        update_category: [
            {
                key: "category",
                title: "Category",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        update_damage: [
            {
                key: "damage",
                title: "Damage",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.previous_damage;
                    const now = otherFields?.damage;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "new_risk",
                title: "New Risk",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        update_description: [
            {
                key: "description",
                title: "Description",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPlainText,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        update_file: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        update_likelihood: [
            {
                key: "likelihood",
                title: "Likelihood",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.previous_likelihood;
                    const now = otherFields?.likelihood;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "new_risk",
                title: "New Risk",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            }
        ],
        update_title: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            },
            {
                key: "title",
                title: "Title",
                hideIfValueIsUndefined: true,
            }
        ],
        upload_file: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        upload_files: [
            {
                key: "files",
                title: "Files",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    software: {
        add_dpa_attachment: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        add_hosting_location: [
            {
                key: "added_hosting_location",
                title: "Added Location",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                            key: "software_secondary_id",
                            title: "Software Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        add_legal_entity: [
            {
                key: "legal_entity_name",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            },
            {
                key: "software_secondary_id",
                title: "Software ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
            },
            {
                            key: "legal_entity_secondary_id",
                            title: "Legal Entity Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        add_module: [
            {
                key: "module",
                title: "Module",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        add_none_eu_hosting_location: [
            {
                key: "added_none_eu__hosting_location",
                title: "Added Location",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                            key: "software_secondary_id",
                            title: "Software Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        add_owner: [
            {
                key: "added_owner_id",
                title: "Added Owner Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "added_owner_name",
                title: "Added Owner Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        add_processing_activity_module_name: [
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        add_relevancy: [
            {
                key: "reason_text",
                title: "Reason Text",
                hideIfValueIsUndefined: true,
            },
            {
                key: "reason_type",
                title: "Reason Type",
                hideIfValueIsUndefined: true,
            },
            {
                key: "relevancy",
                title: "Relevancy",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        ai_generate_dpia: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        ai_generate_dpia_impact_assessment: [
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_analyst: [
            {
                key: "changed_user_name",
                title: "Analyst",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.previous_analyst_name;
                    const now = otherFields?.analyst_name ?? otherFields?.changed_user_name;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_asset_notice: [
            {
                key: "changed_notice_from",
                title: "Old Value",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                key: "changed_notice_to",
                title: "New Value",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                            key: "software_secondary_id",
                            title: "Software Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        change_asset_type: [
            {
                key: "changed_type_from",
                title: "Old Type",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                key: "changed_type_to",
                title: "New Type",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                            key: "software_secondary_id",
                            title: "Software Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        change_company: [
            {
                key: "company",
                title: "Company",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_email: [
            {
                key: "email",
                title: "Email",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_intended_purpose: [
            {
                key: "changed_intended_purpose_to",
                title: "Intended Purpose",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.changed_intended_purpose_from;
                    const now = otherFields?.changed_intended_purpose_to;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_phone_number: [
            {
                key: "phone_number",
                title: "Phone Number",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_position: [
            {
                key: "position",
                title: "Position",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_priority: [
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAssetStatusWasNow,
            },
            {
                key: "status",
                title: "Priority",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPriorityWasNow,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_processing_activity: [
            {
                key: "module_name",
                title: "Module",
                hideIfValueIsUndefined: true,
            },
            {
                key: "questionnaire",
                title: "Questionnaire",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                        undefined;
                },
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
            {
                            key: "module_responsible_person",
                            title: "Module Responsible Person",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "questionnaire",
                            title: "Questionnaire",
                            hideIfValueIsUndefined: true,
                            valueRenderer: ({ otherFields, t }) => {
                                return getHistoryLogQuestionnaireLabel(otherFields, t) ||
                                    undefined;
                            },
                        },
            {
                            key: "report_case_id",
                            title: "Report Case Id",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "software_secondary_id",
                            title: "Software Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        change_responsible_department: [
            {
                key: "department",
                title: "Department",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_responsible_person: [
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "user",
                title: "User",
                hideIfValueIsUndefined: true,
            }
        ],
        change_statement_link: [
            {
                key: "link",
                title: "Link",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        change_status: [
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAssetStatusWasNow,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        create: [
            {
                key: "software_ids",
                title: "Created Software IDs",
                hideIfValueIsUndefined: true,
            }
        ],
        create_ai_processing_activity: [
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        create_asset_document: [
            {
                key: "asset_document_name",
                title: "Uploaded Document",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                key: "asset_document_size",
                title: "Asset Document Size",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogFileSize,
            },
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        create_data_flow: [
    {
        key: "data_type",
        title: "Data Type",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ otherFields }) => {
            return otherFields?.data_type || "-";
        },
    },
    {
        key: "data_subtype",
        title: "Data Subtype",
        hideIfValueIsUndefined: true,
    },
    {
        key: "direction",
        title: "Direction",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ value, t }) => {
            const titles = {
                receives_from: "Receives From",
                sends_to: "Sends To",
            };
            return value ? t(titles[value] || value) : undefined;
        },
    },
    {
        key: "software_secondary_id",
        title: "Software ID",
        hideIfValueIsUndefined: true,
    },
],
        create_processing_activity: [
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        delete: [
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        delete_asset_document: [
            {
                key: "asset_document_name",
                title: "Removed Document",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        delete_data_flow: [
    {
        key: "software_secondary_id",
        title: "Software ID",
        hideIfValueIsUndefined: true,
    },
    {
        key: "data_flow_id",
        title: "Data Flow ID",
        hideIfValueIsUndefined: true,
    },
],
        delete_owner: [
            {
                key: "deleted_owner_id",
                title: "Deleted Owner Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "deleted_owner_name",
                title: "Deleted Owner Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        download: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        dpia: [
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        edit: [
            {
                key: "new_provider",
                title: "Provider",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_provider;
                    const now = otherFields?.new_provider;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "new_version",
                title: "Version",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_version;
                    const now = otherFields?.new_version;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "new_operating_system",
                title: "Operating System",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.old_operating_system;
                    const now = otherFields?.new_operating_system;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        fetch_software: [
            {
                key: "new_software_names",
                title: "Fetched Software List",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value?.length ? value.join(", ") : undefined;
                },
            },
            {
                            key: "software_count",
                            title: "Software Count",
                            hideIfValueIsUndefined: true,
                        }
        ],
        multi_approve: [
            {
                key: "software_ids",
                title: "Created Software IDs",
                hideIfValueIsUndefined: true,
            }
        ],
        news_toggle_read_flag: [
            {
                key: "flag",
                title: "Flag",
                hideIfValueIsUndefined: true,
            },
            {
                key: "news_id",
                title: "News Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_hosting_location: [
            {
                key: "removed_hosting_location",
                title: "Removed Location",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                            key: "software_secondary_id",
                            title: "Software Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        remove_legal_entity: [
            {
                key: "legal_entity_name",
                title: "Legal Entity",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_risk_id",
                title: "Risk ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogRiskId,
            },
            {
                key: "software_secondary_id",
                title: "Software ID",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
            },
            {
                            key: "legal_entity_secondary_id",
                            title: "Legal Entity Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        remove_module: [
            {
                key: "module",
                title: "Module",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        remove_none_eu_hosting_location: [
            {
                key: "removed_none_eu_hosting_location",
                title: "Removed Location",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, t }) => {
                    return value ? t(value) : undefined;
                },
            },
            {
                            key: "software_secondary_id",
                            title: "Software Id",
                            hideIfValueIsUndefined: true,
                        }
        ],
        remove_processing_activity: [
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        toggle_dpa_consultation_required: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        toggle_flag: [
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        toggle_inactive_status: [
            {
                key: "is_inactive",
                title: "Is Inactive",
                hideIfValueIsUndefined: true,
            },
            {
                key: "message",
                title: "Message",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        update_ai_risk_assessment: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_asset_document: [
            {
                key: "asset_document_name",
                title: "Asset Document Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "asset_document_size",
                title: "Asset Document Size",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogFileSize,
            },
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
        ],
        update_conformity_assessment: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_data_flow: [
    {
        key: "data_type",
        title: "Data Type",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ otherFields }) => {
            return otherFields?.data_type || "-";
        },
    },
    {
        key: "data_subtype",
        title: "Data Subtype",
        hideIfValueIsUndefined: true,
    },
    {
        key: "direction",
        title: "Direction",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ value, t }) => {
            const titles = {
                receives_from: "Receives From",
                sends_to: "Sends To",
            };
            return value ? t(titles[value] || value) : undefined;
        },
    },
    {
        key: "software_secondary_id",
        title: "Software ID",
        hideIfValueIsUndefined: true,
    },
],
        update_data_protection_impact_assessment: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_description_of_processing: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_determining_the_processing: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_dpa_date: [
    {
        key: "date",
        title: "DPA Date",
        hideIfValueIsUndefined: true,
        valueRenderer: renderHistoryLogDateTime,
    },
    {
        key: "software_secondary_id",
        title: "Software ID",
        hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
    },
    {
        key: "other_asset_secondary_id",
        title: "Asset ID",
        hideIfValueIsUndefined: true,
    },
],
        update_dpa_reason: [
    {
        key: "reason",
        title: "Reason",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ value }) => {
            if (!value) {
                return undefined;
            }
            return (
                <span className="detail-value" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {value}
                </span>
            );
        },
    },
    {
        key: "software_secondary_id",
        title: "Software ID",
        hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
    },
    {
        key: "other_asset_secondary_id",
        title: "Asset ID",
        hideIfValueIsUndefined: true,
    },
],
        update_dpa_result: [
    {
        key: "result",
        title: "Result",
        hideIfValueIsUndefined: true,
        valueRenderer: ({ value }) => {
            if (!value) {
                return undefined;
            }
            return (
                <span className="detail-value" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {value}
                </span>
            );
        },
    },
    {
        key: "software_secondary_id",
        title: "Software ID",
        hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    return value ? `A${value}` : undefined;
                },
    },
    {
        key: "other_asset_secondary_id",
        title: "Asset ID",
        hideIfValueIsUndefined: true,
    },
],
        update_fria_assessment: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        update_processing_activity_module_name: [
            {
                key: "to_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.from_name;
                    const now = otherFields?.to_name;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        update_responsible_person: [
            {
                key: "to_user",
                title: "Responsible Person",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.from_user;
                    const now = otherFields?.to_user;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "module_name",
                title: "Module Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "module_responsible_person",
                title: "Module Responsible Person",
                hideIfValueIsUndefined: true,
            },
            {
                key: "report_case",
                title: "Report Case Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "software_secondary_id",
                title: "Software Id",
                hideIfValueIsUndefined: true,
            }
        ],
        update_risk_assessment: [
            {
                key: "software_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "changes",
                title: "Changes",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    return renderHistoryLogQuestionnaireChanges(otherFields, t);
                },
            },
        ],
        upload_csv: [
            {
                key: "software_ids",
                title: "Created Software IDs",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    task: {
        add_control: [
            {
                key: "added_norm",
                title: "Added Control",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            },
            {
                key: "soa_version_id",
                title: "SOA Version Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "soa_version_number",
                title: "SOA Version Number",
                hideIfValueIsUndefined: true,
            }
        ],
        assign_analyst: [
            {
                key: "analyst_name",
                title: "Analyst",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) => {
                    const was = otherFields?.previous_analyst_name;
                    const now = otherFields?.analyst_name ?? otherFields?.changed_user_name;
                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {t(String(was))} | {t("After")}: {t(String(now))}
                            </>
                        );
                    }
                    if (typeof now !== "undefined") {
                        return t(String(now));
                    }
                    if (typeof was !== "undefined") {
                        return t(String(was));
                    }
                    return undefined;
                },
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        create: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        create_comment: [
            {
                key: "attachments",
                title: "Attachments",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "comment",
                title: "Comment",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPlainText,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        delete: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        delete_file: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        download_file: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        download_files: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        remove_control: [
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "removed_norm",
                title: "Removed Norm",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            },
            {
                key: "soa_version_id",
                title: "SOA Version Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "soa_version_number",
                title: "SOA Version Number",
                hideIfValueIsUndefined: true,
            }
        ],
        update_category: [
            {
                key: "category",
                title: "Category",
                hideIfValueIsUndefined: true,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        update_deadline: [
            {
                key: "previous_deadline",
                title: "Previous Deadline",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogDateTime,
            },
            {
                key: "deadline",
                title: "Updated Deadline",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogDateTime,
            },
            {
                            key: "other_asset_secondary_id",
                            title: "Asset ID",
                            hideIfValueIsUndefined: true,
                        },
            {
                            key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
                        }
        ],
        update_description: [
            {
                key: "description",
                title: "Description",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPlainText,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        update_file: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        update_priority: [
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskStatusWasNow,
            },
            {
                key: "status",
                title: "Priority",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogPriorityWasNow,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        update_status: [
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskStatusWasNow,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_task_id",
                title: "Task ID",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogTaskId,
            }
        ],
        upload_file: [
            {
                key: "file_id",
                title: "File Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "file_name",
                title: "File Name",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
        upload_files: [
            {
                key: "files",
                title: "Files",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogAttachments,
            },
            {
                key: "other_asset_secondary_id",
                title: "Asset ID",
                hideIfValueIsUndefined: true,
            }
        ],
    },
    tom: {
        add_measure: [
            {
                key: "measure_id",
                title: "Measure ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "measure_title",
                title: "Measure Title",
                hideIfValueIsUndefined: true,
            },
            {
                key: "measure_type",
                title: "Measure Type",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogCapitalizedValue,
            },
            {
                key: "sub_section_title",
                title: "Sub-section",
                hideIfValueIsUndefined: true,
            },
        ],
        add_subcontractor: [
            {
                key: "subcontractor_id",
                title: "Subcontractor ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "name",
                title: "Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "area",
                title: "Area",
                hideIfValueIsUndefined: true,
            },
            {
                key: "address",
                title: "Address",
                hideIfValueIsUndefined: true,
            },
            {
                key: "website",
                title: "Website",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_tom_id",
                title: "TOM Id",
                hideIfValueIsUndefined: true,
            },
        ],
        change_analyst: [
            {
                key: "user",
                title: "Analyst",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, users }) => {
                    if (!value) {
                        return undefined;
                    }
                    if (!users?.length) {
                        return value;
                    }
                    const user = users.find((item) => String(item.id) === String(value));
                    return user ? `${user.first_name} ${user.last_name}` : value;
                },
            },
            {
                key: "previous_analyst_name",
                title: "Previous Analyst Name",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_tom_id",
                title: "TOM Id",
                hideIfValueIsUndefined: true,
            },
        ],
        change_description: [
            {
                key: "sub_section_title",
                title: "Sub-section",
                hideIfValueIsUndefined: true,
            },
            {
                key: "description",
                title: "Description",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogLongTextWasNow({
                        otherFields,
                        t,
                        previousKey: "previous_description",
                        currentKey: "description",
                    }),
            },
        ],
        change_information: [
            {
                key: "sub_section_title",
                title: "Sub-section",
                hideIfValueIsUndefined: true,
            },
            {
                key: "information",
                title: "Information",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogLongTextWasNow({
                        otherFields,
                        t,
                        previousKey: "previous_information",
                        currentKey: "information",
                    }),
            },
        ],
        change_legal_entities: [
            {
                key: "legal_entity_names",
                title: "Legal Entities",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value }) => {
                    if (!value?.length) {
                        return undefined;
                    }
                    return (
                        <div>
                            {value.map((name, index) => {
                                return (
                                    <div className="detail-value d-flex" key={index}>
                                        • {name}
                                    </div>
                                );
                            })}
                        </div>
                    );
                },
            },
            {
                key: "legal_entities",
                title: "Legal Entities",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_tom_id",
                title: "TOM Id",
                hideIfValueIsUndefined: true,
            },
        ],
        change_revision: [
            {
                key: "date",
                title: "Revision Date",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogDateTime,
            },
            {
                key: "previous_date",
                title: "Previous Date",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogDateTime,
            },
            {
                key: "secondary_tom_id",
                title: "TOM Id",
                hideIfValueIsUndefined: true,
            },
        ],
        change_status: [
            {
                key: "secondary_tom_id",
                title: "TOM ID",
                hideIfValueIsUndefined: true,
            },
            {
                key: "status",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields, t }) => {
                    const formatStatus = (statusValue) => {
                        const config = TOM_STATUS_LIST[statusValue?.toUpperCase()];
                        return config ? t(config.title) : statusValue;
                    };
                    const was = otherFields?.previous_status;
                    const now = value ?? otherFields?.status;

                    if (typeof was !== "undefined" && typeof now !== "undefined") {
                        return (
                            <>
                                {t("Before")}: {formatStatus(was)} | {t("After")}: {formatStatus(now)}
                            </>
                        );
                    }

                    if (typeof now !== "undefined") {
                        return formatStatus(now);
                    }

                    return undefined;
                },
            },
        ],
        create: [
            {
                key: "secondary_tom_id",
                title: "TOM Id",
                hideIfValueIsUndefined: true,
            },
        ],
        delete_subcontractor: [
            {
                key: "name",
                title: "Name",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ value, otherFields }) => {
                    return value ?? otherFields?.subcontractor_name;
                },
            },
            {
                key: "area",
                title: "Area",
                hideIfValueIsUndefined: true,
            },
            {
                key: "address",
                title: "Address",
                hideIfValueIsUndefined: true,
            },
            {
                key: "website",
                title: "Website",
                hideIfValueIsUndefined: true,
            },
        ],
        duplicate: [
            {
                key: "source_secondary_tom_id",
                title: "Source TOM Id",
                hideIfValueIsUndefined: true,
            },
            {
                key: "secondary_tom_id",
                title: "TOM Id",
                hideIfValueIsUndefined: true,
            },
        ],
        edit_subcontractor: [
            {
                key: "name",
                title: "Name",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogWasNowValue({
                        otherFields,
                        t,
                        previousKey: "previous_name",
                        currentKey: "name",
                    }),
            },
            {
                key: "area",
                title: "Area",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogWasNowValue({
                        otherFields,
                        t,
                        previousKey: "previous_area",
                        currentKey: "area",
                    }),
            },
            {
                key: "address",
                title: "Address",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogWasNowValue({
                        otherFields,
                        t,
                        previousKey: "previous_address",
                        currentKey: "address",
                    }),
            },
            {
                key: "website",
                title: "Website",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogWasNowValue({
                        otherFields,
                        t,
                        previousKey: "previous_website",
                        currentKey: "website",
                    }),
            },
            {
                key: "gtc",
                title: "GTC",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogWasNowValue({
                        otherFields,
                        t,
                        previousKey: "previous_gtc",
                        currentKey: "gtc",
                    }),
            },
            {
                key: "privacy_policy",
                title: "Privacy Policy",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogWasNowValue({
                        otherFields,
                        t,
                        previousKey: "previous_privacy_policy",
                        currentKey: "privacy_policy",
                    }),
            },
        ],
        remove_measure: [
            {
                key: "measure_title",
                title: "Measure Title",
                hideIfValueIsUndefined: true,
            },
            {
                key: "measure_type",
                title: "Measure Type",
                hideIfValueIsUndefined: true,
                valueRenderer: renderHistoryLogCapitalizedValue,
            },
            {
                key: "sub_section_title",
                title: "Sub-section",
                hideIfValueIsUndefined: true,
            },
        ],
        restore: [
            {
                key: "secondary_tom_id",
                title: "TOM Id",
                hideIfValueIsUndefined: true,
            },
        ],
        toggle_measure: [
            {
                key: "measure_title",
                title: "Measure Title",
                hideIfValueIsUndefined: true,
            },
            {
                key: "is_checked",
                title: "Status",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogBooleanWasNow({
                        otherFields,
                        t,
                        previousKey: "previous_is_checked",
                        currentKey: "is_checked",
                        trueLabel: "Enabled",
                        falseLabel: "Disabled",
                    }),
            },
        ],
        toggle_show_pdf: [
            {
                key: "sub_section_title",
                title: "Sub-section",
                hideIfValueIsUndefined: true,
            },
            {
                key: "show_on_pdf",
                title: "Show on PDF",
                hideIfValueIsUndefined: true,
                valueRenderer: ({ otherFields, t }) =>
                    renderHistoryLogBooleanWasNow({
                        otherFields,
                        t,
                        previousKey: "previous_show_on_pdf",
                        currentKey: "show_on_pdf",
                        trueLabel: "Visible on PDF",
                        falseLabel: "Hidden from PDF",
                    }),
            },
        ],
    },
};

export const HistoryLogActions = {
    admin: {
        activate_framework: "Activated compliance framework",
        add_legal_entities_to_framework: "Added legal entity to framework",
        change_active_directory_settings: "Active directory settings",
        change_default_owner: "Default owner has been changed",
        change_user_status: "Changed user status",
        create_user: "Created user",
        deactivate_framework: "Deactivated compliance framework",
        delete_user: "Deleted user",
        edit_mailer_setting: "Updated mail settings",
        edit_news_setting: "Edit news setting",
        edit_organization: "Edited organization",
        edit_risk_matrix: "Update matrix",
        edit_user: "Edit user",
        login: "Login",
        remove_legal_entities_from_framework: "Removed legal entity from framework",
        remove_option: "Removed option",
        remove_question: "Removed question",
        restore_news_setting: "Restore news setting",
        restore_organization_email_notifications: "Restore organization email notifications",
        restore_risk_matrix: "Restore matrix",
        toggle_mailer_setting_active: "Toggled mail settings active status",
        update_framework_settings: "Updated compliance framework settings",
        update_organization_email_notifications: "Update organization email notifications",
        update_question: "Updated question",
    },
    asset_document: {
        create_asset_document: 'Upload Document',
        delete_asset_document: 'Remove Document',
        update_asset_document: 'Document Updated',
    },
    case: {
        add_comment: "Add Comment",
        add_internal_comment: "Add Internal Comment",
        add_related_asset: "Related asset added",
        add_report_legal_entity: "Added affected legal entity",
        change_admin_category: "Admin category updated",
        change_admin_description: "Admin description updated",
        create: "New case created",
        delete: "Deleted",
        delete_attachment: "Delete Attachment",
        download_analyst_attachment: "Download analyst attachment",
        download_user_attachment: "Download user attachment",
        dsr_generate_lia: "AI Generated LIA Assessment",
        dsr_generate_response: "AI Generated Data Subject Response",
        dsr_update_workflow: "Updated LIA Details",
        link_case: "Linked case updated",
        mark_as_delete: "Marked as delete",
        remove_related_asset: "Related asset removed",
        remove_report_legal_entity: "Removed affected legal entity",
        toggle_test_case: "Toggle test case",
        update_analyst: "Update Analyst",
        update_analyst_readonly: "Read-Only Analysts",
        update_priority: "Priority updated",
        update_questionnaire: "Updated Questionnaire",
        update_risk: "Risk updated",
        update_status: "Update Status",
        upload_attachment: "Uploaded attachment",
    },
    client_download: {
        download_client: 'Download Windows Device Agent',
    },
    control: {
        toggle_applicable: 'Control Applicability Toggled',
        update_justification: 'Control Justification Updated',
    },
    document: {
        create: "Created",
        remove: "Removed",
        remove_category: "Removed category",
    },
    entityManagement: {
        add_bulk_legal_entity: 'Upload Legal List',
        add_division: 'Add Department',
        add_institute: 'Add Institute',
        add_legal_entity: 'Add Legal Entity',
        add_legal_location: 'Add Legal Location',
        clear_routing: 'Routing Deleted',
        delete: 'Delete Legal Entity',
        edit_division: 'Edit Department',
        edit_financial_setting: 'Edit Financial Year Settings',
        edit_institute: 'Edit Institute',
        edit_legal_entity: 'Edit Legal Entity',
        edit_legal_location: 'Edit Legal Location',
        edit_routing: 'Edit Routing Settings',
        force_delete: 'Force Delete Legal Entity',
        force_remove_legal_entity: "Force removed legal entity",
        remove_division: 'Remove Department',
        remove_institute: 'Remove Institute',
        remove_legal_entity: 'Remove Legal Entity',
        remove_legal_location: 'Remove Legal Location',
    },
    hardware: {
        add_legal_entity: "Added legal entity",
        change_analyst: "Change Analyst Assigned",
        change_priority: "Change Priority",
        change_status: "Change Status",
        create: "Created",
        delete: "Deleted",
        download: "Downloaded",
        edit: "Edit detail",
        handover: "Deliver the hardware to the new user",
        multi_approve: "Multi-approve",
        remove_legal_entity: "Removed legal entity",
        toggle_flag: "Change flag",
        upload_csv: "Upload hardware data from Excel file",
    },
    other_asset: {
        add_comment: "Added Comment to Other Asset",
        add_dpa_attachment: "Added DPA Attachment to Other Asset",
        add_internal_comment: "Added Internal Comment to Other Asset",
        add_legal_entity: "Added Legal Entity to Other Asset",
        add_module: "Added module",
        add_processing_activity_module_name: "Added Processing Activity Module Name",
        add_related_asset: "Added Related Asset to Other Asset",
        add_report_legal_entity: "Added Legal Entity to Other Asset Report",
        change_admin_category: "Changed Other Asset Admin Category",
        change_admin_description: "Changed Other Asset Admin Description",
        change_analyst: "Changed Other Asset Analyst",
        change_company: "Changed Other Asset Company",
        change_email: "Changed Other Asset Email",
        change_phone_number: "Changed Other Asset Phone Number",
        change_position: "Changed Other Asset Position",
        change_priority: "Changed Other Asset Priority",
        change_processing_activity: "Changed Processing Activity",
        change_responsible_department: "Changed Other Asset Responsible Department",
        change_responsible_person: "Changed Other Asset Responsible Person",
        change_statement_link: "Changed Other Asset Statement Link",
        change_status: "Changed Other Asset Status",
        create: "Created Other Asset",
        create_ai_processing_activity: "Created Processing Activity with AI",
        create_processing_activity: "Created Processing Activity",
        delete: "Deleted Other Asset",
        delete_asset_document: "Deleted Document from Other Asset",
        delete_attachment: "Deleted Attachment from Other Asset",
        download: "Exported Other Assets as Excel",
        download_analyst_attachment: "Downloaded Analyst Attachment from Other Asset",
        download_user_attachment: "Downloaded User Attachment from Other Asset",
        dpia: "Created DPIA for Other Asset",
        edit: "Edited Other Asset",
        link_case: "Linked Case to Other Asset",
        mark_as_delete: "Marked Other Asset as Deleted",
        multi_approve: "Approved Multiple Other Assets",
        remove_legal_entity: "Removed Legal Entity from Other Asset",
        remove_module: "Removed module",
        remove_processing_activity: "Removed Processing Activity",
        remove_related_asset: "Removed Related Asset from Other Asset",
        remove_report_legal_entity: "Removed Legal Entity from Other Asset Report",
        toggle_dpa_consultation_required: "Changed DPA Consultation Requirement for Other Asset",
        toggle_flag: "Changed Other Asset Flag Status",
        toggle_inactive_status: "Changed Other Asset Inactive Status",
        toggle_test_case: "Toggled Other Asset Test Case",
        update: "Updated Other Asset",
        update_analyst: "Updated Other Asset Analyst",
        update_analyst_readonly: "Updated Other Asset Analyst (Read-only)",
        update_data_protection_impact_assessment: "Updated Data Protection Impact Assessment",
        update_description_of_processing: "Updated Description of Processing",
        update_determining_the_processing: "Updated Determining the Processing",
        update_dpa_date: "Updated DPA Date for Other Asset",
        update_dpa_reason: "Updated DPA Reason for Other Asset",
        update_dpa_result: "Updated DPA Result for Other Asset",
        update_processing_activity_module_name: "Updated Processing Activity Module Name",
        update_responsible_person: "Updated Other Asset Responsible Person",
        update_risk: "Updated Risk for Other Asset",
        update_risk_assessment: "Updated Risk Assessment",
        update_status: "Updated Other Asset Status",
        upload_attachment: "Uploaded Attachment to Other Asset",
    },
    questionnaire: {
        remove_option: "Removed option",
        remove_question: "Removed question",
        update_question: "Updated question",
    },
    risk: {
        add_legal_entity: "Legal entity added",
        assign_analyst: "Assigned to analyst",
        create: "New risk created",
        create_comment: "New comment created",
        delete: "Deleted",
        delete_file: "Delete file",
        download_file: "Download file",
        download_files: "Download files",
        remove_legal_entity: "Legal entity removed",
        update_acceptance: "Risk Accepted",
        update_category: "Category updated",
        update_damage: "Damage updated",
        update_description: "Description updated",
        update_file: "Upload file",
        update_likelihood: "Likelihood updated",
        update_title: "Title Updated",
        upload_file: "Uploaded file",
        upload_files: "Uploaded files",
    },
    software: {
        add_dpa_attachment: "Added DPA Attachment",
        add_hosting_location: 'Added a new hosting location',
        add_legal_entity: "Added legal entity",
        add_module: "Added a new module",
        add_none_eu_hosting_location: 'Added a non-EU hosting location',
        add_owner: "Added a new owner",
        add_processing_activity_module_name: 'Added processing activity module name',
        add_relevancy: "Added relevancy",
        ai_generate_dpia: "AI Generate DPIA",
        ai_generate_dpia_impact_assessment: "AI generated DPIA impact assessment",
        change_analyst: "Change Analyst Assigned",
        change_asset_notice: 'Change Asset Notice',
        change_asset_type: 'Change Asset Type',
        change_company: "Change Company",
        change_email: "Change Email",
        change_intended_purpose: "Changed intended purpose",
        change_phone_number: "Change Phone Number",
        change_position: "Change Position",
        change_priority: "Change Priority",
        change_processing_activity: "Change Processing Activity",
        change_responsible_department: "Change Responsible Department",
        change_responsible_person: "Change Responsible Person",
        change_statement_link: "Change Statement Link",
        change_status: "Change Status",
        create: "Created",
        create_ai_processing_activity: 'Processing Activity Created by AI',
        create_asset_document: 'Upload Document',
        create_data_flow: "Data Flow Created",
        create_processing_activity: "Create Processing Activity",
        delete: "Deleted",
        delete_asset_document: 'Remove Document',
        delete_data_flow: "Data Flow Deleted",
        delete_owner: "Deleted an owner",
        download: "Downloaded",
        dpia: "DPIA results",
        edit: "Edit detail",
        fetch_software: 'Fetch Software List',
        multi_approve: "Multi-approve",
        news_toggle_read_flag: "Toggled news read flag",
        remove_hosting_location: 'Removed a hosting location',
        remove_legal_entity: "Removed legal entity",
        remove_module: "Removed a module",
        remove_none_eu_hosting_location: 'Removed a non-EU hosting location',
        remove_processing_activity: "Remove Processing Activity",
        toggle_dpa_consultation_required: "Changed DPA Consultation Requirement",
        toggle_flag: "Change flag",
        toggle_inactive_status: 'Software Activation Status Toggled',
        update_ai_risk_assessment: "Updated AI Risk Assessment",
        update_asset_document: 'Document Updated',
        update_conformity_assessment: "Updated Conformity Assessment",
        update_data_flow: "Data Flow Updated",
        update_data_protection_impact_assessment: "Updated Data Protection Impact Assessment",
        update_description_of_processing: "Updated Description of Processing",
        update_determining_the_processing: "Updated Determining the Processing",
        update_dpa_date: "Updated DPA Date",
        update_dpa_reason: "Updated DPA Reason",
        update_dpa_result: "Updated DPA Result",
        update_fria_assessment: "Updated FRIA Assessment",
        update_processing_activity_module_name: 'Updated processing activity module name',
        update_responsible_person: 'Processing Activity Responsible Person Updated',
        update_risk_assessment: "Updated Risk Assessment",
        upload_csv: "Upload software data from Excel file",
    },
    task: {
        add_control: "Control added",
        assign_analyst: "Assigned to analyst",
        create: "New task created",
        create_comment: "New comment created",
        delete: "Deleted",
        delete_file: "Delete file",
        download_file: "Download file",
        download_files: "Download files",
        remove_control: "Control removed",
        update_category: "Category updated",
        update_deadline: "Deadline updated",
        update_description: "Description updated",
        update_file: "Upload file",
        update_priority: "Priority updated",
        update_status: "Status updated",
        upload_file: "Uploaded file",
        upload_files: "Uploaded files",
    },
    tom: {
        add_measure: "Measure added",
        add_subcontractor: "Subcontractor added",
        change_analyst: "Analyst changed",
        change_description: "Description changed",
        change_information: "Information changed",
        change_legal_entities: "Legal entities changed",
        change_revision: "Revision date changed",
        change_status: "Status changed",
        create: "TOM created",
        delete_subcontractor: "Subcontractor deleted",
        duplicate: "TOM duplicated",
        edit_subcontractor: "Subcontractor edited",
        remove_measure: "Measure removed",
        restore: "Restored",
        toggle_measure: "Measure toggled",
        toggle_show_pdf: "Show on PDF toggled",
    },
};

export const HistoryLogSections = {
    risk: "Risk Management",
    task: "Task Management",
    admin: "Administration",
    case: "Case Management",
    document: "Document Management",
    user: "User",
    software: "Software Management",
    hardware: "Hardware Management",
    questionnaire: "Questionnaire Management",
    tom: "TOM Management",
    entityManagement: "Entity Management",
    asset_document: "Document Management",
    client_download: "Integration Settings",
    control: "Control Management",
    other_asset: "Other Asset Management"
};

export const HistoryLogRoles = {
    ROLE_ANALYST_ADMIN: "Analyst admin",
    ROLE_ANALYST: "Analyst",
    ROLE_TASK_OWNER: "Task Owner",
    ROLE_THIRD_PARTY: "Third Party",
    ROLE_EMPLOYEE: "Employee",
};
